import { Box, MenuItem, Select, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons"

import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

const ROWS_PER_PAGE_OPTIONS: readonly number[] = [10, 20, 50]

const useStyles = makeStyles()((theme) => ({
    paginationContainer: {
        marginTop: ".5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    resultsPerPage: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    label: {
        fontSize: ".875rem",
        color: theme.palette.tailwind.slate[950],
    },
    rowsPerPageSelect: {
        fontSize: ".875rem",
        color: theme.palette.tailwind.slate[950],
    },
    navigation: {
        display: "flex",
        alignItems: "center",
        gap: "1.25rem",
    },
    navButtonsGroup: {
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
    },
    navButton: {
        borderRadius: ".5rem !important",
        minWidth: "2.5rem",
        width: "2.5rem",
        height: "2.5rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        "&:disabled": {
            opacity: 0.5,
        },
    },
    navIcon: {
        color: theme.palette.tailwind.slate[900],
    },
}))

type MeetingListPaginationProps = Readonly<{
    totalItems: number
    rowsPerPage: number
    currentPage: number
    setRowsPerPage: (rows: number) => void
    setCurrentPage: (page: number) => void
}>

export function MeetingListPagination({
    totalItems,
    rowsPerPage,
    currentPage,
    setRowsPerPage,
    setCurrentPage,
}: MeetingListPaginationProps) {
    const { classes } = useStyles()

    if (totalItems <= Math.min(...ROWS_PER_PAGE_OPTIONS)) return null

    const totalPages = Math.ceil(totalItems / rowsPerPage)
    const startItem = (currentPage - 1) * rowsPerPage + 1
    const endItem = Math.min(startItem + rowsPerPage - 1, totalItems)

    function handlePreviousPage() {
        setCurrentPage(currentPage - 1)
    }

    function handleNextPage() {
        setCurrentPage(currentPage + 1)
    }

    return (
        <Box className={classes.paginationContainer}>
            <Box className={classes.resultsPerPage}>
                <LocalizedStrict id="service-center-reports-dashboard-meeting-pagination">
                    <Typography variant="body2" className={classes.label}>
                        Results per page
                    </Typography>
                </LocalizedStrict>
                <Select
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    variant="standard"
                    disableUnderline
                    className={classes.rowsPerPageSelect}
                >
                    {ROWS_PER_PAGE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box className={classes.navigation}>
                <Typography variant="body2" className={classes.label}>
                    {startItem}-{endItem} of {totalItems}
                </Typography>

                <Box className={classes.navButtonsGroup}>
                    <NavigationButton icon={faChevronLeft} disabled={currentPage === 1} onClick={handlePreviousPage} />
                    <NavigationButton
                        icon={faChevronRight}
                        disabled={currentPage === totalPages}
                        onClick={handleNextPage}
                    />
                </Box>
            </Box>
        </Box>
    )
}

type NavigationButtonProps = Readonly<{
    icon: typeof faChevronLeft
    disabled: boolean
    onClick: () => void
}>

function NavigationButton({ icon, disabled, onClick }: NavigationButtonProps) {
    const { classes } = useStyles()

    return (
        <StandardButton
            variant="text"
            size="small"
            fullWidth={false}
            className={classes.navButton}
            onClick={onClick}
            disabled={disabled}
        >
            <FontAwesomeIcon icon={icon} size="lg" className={classes.navIcon} />
        </StandardButton>
    )
}
